// Every string that can reach `SET_VIZ_STATUS`, in one place.
//
// These used to be 14 inline literals spread across useModalViz.ts,
// useAutoSim.ts and RoomShell.tsx, and two of them had rotted into naming
// buttons that do not exist: "voxel pending — finish room" leaked the
// internal name for the eigensolve gate, and "scene changed — Scan to
// update" pointed at a "Scan" button that was renamed to Compute
// (ScanButton.tsx) some time ago. Neither was catchable by a test while the
// strings lived at their dispatch sites, which is the actual reason this
// module exists — `web/tests/unit/vizStatusCopy.test.ts` can only enforce
// the wording if there is one place to enforce it against.
//
// House style for this surface: sentence case, one clause, and if the user
// has to do something, name the control they have to click exactly as it is
// labelled on screen.

import { pointerVerb } from './pointerVerb';

/** The Compute button's on-screen label (ScanButton.tsx). Referenced rather
 *  than re-typed so a future rename has one place to touch and the copy
 *  cannot drift back into naming a control that isn't there. */
const COMPUTE_LABEL = 'Compute';

export const VIZ_STATUS = {
  /** No chip at all. The reducer also resolves to this on SET_VIZ 'none'. */
  none: '',

  /** The voxel eigensolve has not been armed for the current geometry, so
   *  nothing can be solved yet. Terminal until the user acts. A getter, so
   *  the verb is read at dispatch: "tap" on a phone, "click" with a mouse.
   *  Never painted in the viewport notice row (isComputePromptStatus below)
   *  — Compute pulses instead. This string still exists as the button's own
   *  tooltip and `aria-label` source, and reducer/test code keys off it. */
  get simulationPending() {
    return `Simulation pending — ${pointerVerb().verb} ${COMPUTE_LABEL}`;
  },

  /** The scene was edited after the last successful scan, so what is on
   *  screen no longer reflects the room. Terminal until the user acts.
   *  Never painted in the viewport notice row — see `simulationPending`. */
  get sceneChanged() {
    return `Scene changed — ${pointerVerb().verb} ${COMPUTE_LABEL} to update`;
  },

  computingRoomModes: 'Computing room modes…',
  computingModalField: 'Computing modal field…',

  /** `tag` is the ' (before)' / ' (after)' qualifier, empty on an
   *  untreated scene where both sides resolve to the same trace. */
  preloadingField: (tag: string) => `Pre-loading 20–200 Hz field${tag}…`,
  tracingRays: (tag: string) => `Tracing rays${tag}…`,

  modalReady: (modesUsed: number, speakersUsed: number, speakersTotal: number, view: string) =>
    `${modesUsed} modes · ${speakersUsed}/${speakersTotal} speakers · ${view}`,
  /** Never show warnings or errors in the 3D viewport notification chip. */
  volumeWarnings: (_count: number) => '',
  rayPoints: (_points: number, _suffix: string) => '',

  /** Never show error messages in the 3D viewport notification chip. */
  failed: (_err: unknown, _fallback: VizFailure) => '',
} as const;

/** The two statuses that tell the user to press Compute. These used to
 *  surface as a floating pill above the viewport control bar — a shape that
 *  reads as a second button popping up on every edit, easy to mistake for
 *  the real Compute button and jarring on a scene that gets edited a lot.
 *  2026-09-16: dropped from the notice row entirely (SceneVizBar) in favour
 *  of Compute itself pulsing — see ScanButton.tsx's `ed-compute-pulse`. This
 *  predicate is what SceneVizBar filters on, and what ScanButton uses to
 *  pick its own tooltip text back up. */
export function isComputePromptStatus(status: string): boolean {
  return status === VIZ_STATUS.sceneChanged || status === VIZ_STATUS.simulationPending;
}

export type VizFailure = 'modal' | 'volume' | 'velocity' | 'raytrace' | 'share';

/** Closed ``error_code`` for viz fetch failures (PostHog ``viz_failed``). */
export function vizFailureCode(kind: VizFailure): string {
  switch (kind) {
    case 'modal':
      return 'viz_modal';
    case 'volume':
    case 'velocity':
      return 'viz_volume';
    case 'raytrace':
      return 'viz_raytrace';
    case 'share':
      return 'viz_share';
    default:
      return 'unknown';
  }
}
